import { useMemo, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import styles from './styles.module.css'

export interface CategoryTestResultItem {
  questionObjectId: string
  query: string
  expectedAllowed: boolean
  actualAllowed: boolean
  matchedCategory: string | null
  reasoning: string
  passed: boolean
  answerQuality: boolean | null
  actualAnswer: string | null
  judgeReasoning: string | null
}

interface TestResultsTableProps {
  results: CategoryTestResultItem[]
  isLoading: boolean
  summary: {
    total: number
    passed: number
    failed: number
    errorCount: number
    pipelineTestsRun?: number
    pipelineTestsPassed?: number
  } | null
}

function ExpandableCell({ text, maxLength = 80 }: { text: string; maxLength?: number }) {
  const [expanded, setExpanded] = useState(false)

  if (!text || text.length <= maxLength) {
    return <span>{text || '-'}</span>
  }

  return (
    <span
      className={styles.reasoningCell}
      onClick={(e) => {
        e.stopPropagation()
        setExpanded(!expanded)
      }}
    >
      {expanded ? text : `${text.slice(0, maxLength)}...`}
    </span>
  )
}

function TestResultsTable({ results, isLoading, summary }: TestResultsTableProps) {
  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: 'Question',
        accessor: 'query',
      },
      {
        Header: 'Expected',
        Cell: ({ row }: any) =>
          row.original.expectedAllowed ? 'Allow' : 'Block',
      },
      {
        Header: 'Actual',
        Cell: ({ row }: any) => {
          const { actualAllowed, passed } = row.original
          return (
            <span className={passed ? styles.passed : styles.failed}>
              {actualAllowed ? 'Allow' : 'Block'} {passed ? '\u2705' : '\u274C'}
            </span>
          )
        },
      },
      {
        Header: 'Matched Category',
        Cell: ({ row }: any) => row.original.matchedCategory || '-',
      },
      {
        Header: 'Reasoning',
        Cell: ({ row }: any) => (
          <ExpandableCell text={row.original.reasoning} />
        ),
      },
      {
        Header: 'Answer Quality',
        Cell: ({ row }: any) => {
          const { answerQuality } = row.original
          if (answerQuality === null) return <span className={styles.notApplicable}>-</span>
          return (
            <span className={answerQuality ? styles.passed : styles.failed}>
              {answerQuality ? '\u2705' : '\u274C'}
            </span>
          )
        },
      },
      {
        Header: 'Actual Answer',
        Cell: ({ row }: any) => {
          if (row.original.actualAnswer === null) return <span className={styles.notApplicable}>-</span>
          return <ExpandableCell text={row.original.actualAnswer} maxLength={100} />
        },
      },
      {
        Header: 'Judge Reasoning',
        Cell: ({ row }: any) => {
          if (row.original.judgeReasoning === null) return <span className={styles.notApplicable}>-</span>
          return <ExpandableCell text={row.original.judgeReasoning} />
        },
      },
    ],
    [],
  )

  return (
    <>
      {summary && (
        <div className={styles.summaryBar}>
          <span className={summary.passed === summary.total ? styles.passed : styles.failed}>
            {summary.passed}/{summary.total} passed
          </span>
          {summary.errorCount > 0 && (
            <span className={styles.failed}> ({summary.errorCount} errors)</span>
          )}
          {summary.pipelineTestsRun !== undefined && summary.pipelineTestsRun > 0 && (
            <span style={{ marginLeft: 12 }}>
              Pipeline: {summary.pipelineTestsPassed}/{summary.pipelineTestsRun} quality passed
            </span>
          )}
        </div>
      )}
      <Table
        columns={columns}
        data={results}
        isLoading={isLoading}
        isDisplayMode={true}
        noDataMessage='No test results yet.'
      />
    </>
  )
}

export default TestResultsTable
